import { getObjectByCache } from '@/utils/basicDataCache'
import { basicCacheKeys } from '@/utils/cacheConfig'
import ChargeUtils from '@/class/cargo/model/utils/ChargeUtils'
import * as ChargeMeasure from '@/class/cargo/model/parameters/ChargeMeasure'
import * as PriceConstant from '@/class/cargo/model/parameters/PriceConstant'
import { QuotationFclDetail } from '@/class/model/price/QuotationFclDetail'
import { QuotationLclDetail } from '@/class/model/price/QuotationLclDetail'
import { QuotationAirDetail } from '@/class/model/price/QuotationAirDetail'
import { Message } from '@/utils/Message'
import $store from '@/store'
import StrUtils from '@/utils/StrUtils'
import { Charge } from '@/class/model/act/Charge'
import { SeaEQ } from '@/class/model/booking/SeaEQ'
import * as RateUtil from '@/class/cargo/model/utils/RateUtil'
import { Company } from '@/class/model/mdm/Company'
import { PriceAirUtil } from '@/class/cargo/model/utils/PriceAirUtil'
import { PriceLclUtil } from '@/class/cargo/model/utils/PriceLclUtil'
import lang from '@/lang'
import { queryPriceLclByKey } from '@/api/price/priceLcl'
import { cache } from '@/utils/cacheManager'
import { multiply } from '@/utils/commonUtil'
import * as SystemConstants from '@/class/cargo/model/parameters/SystemConstants'
import * as EQSize from '@/class/cargo/model/parameters/EQSize'
import { EQ20_EQ_SIZE, EQ40_EQ_SIZE, EQ45_EQ_SIZE } from '@/class/cargo/model/parameters/EQSize'
import {
  SEA_EQ_TYPE_20GP, SEA_EQ_TYPE_20NOR,
  SEA_EQ_TYPE_40GP,
  SEA_EQ_TYPE_40HC, SEA_EQ_TYPE_40NOR,
  SEA_EQ_TYPE_45HC
} from '@/class/cargo/model/parameters/SystemConstants'

export class BookingUtil {
  constructor() {
  }

  /**
   * 转换成job
   */
  static async convertQuotationToJob(job, quotation, quotationDetail) {
    this.getQuotationBasinInfo(quotation, job, quotationDetail)
    if (quotation.type === PriceConstant.QUOTATION_FCL) {
      if (quotationDetail) {
        // 生成应收费用
        if (!await this.generateArChargeByQuotationFCL(new QuotationFclDetail(quotationDetail), quotation, job)) {
          return false
        }
        // 生成应付费用
        if (!await this.generateApChargeByQuotationFCL(new QuotationFclDetail(quotationDetail), quotation, job)) {
          return false
        }
        // 本地费用
        await this.getQuotationChargeDetail(quotation, job, true)
        await this.getQuotationChargeDetail(quotation, job, false)
      }
    } else if (quotation.type === PriceConstant.QUOTATION_LCL) { // 拼箱
      // 报价单没有录入重量和体积
      if (!quotation || isNaN(quotation.grossWeight) || isNaN(quotation.cubage) || quotation.grossWeight < 0 || quotation.cubage < 0) {
        Message.warning(lang.t('Pricing.QuotationWeightAndCubageLessZero'))
        return
      }
      quotation.quotationLclDetail = new QuotationLclDetail(quotationDetail)
      let priceLcl = null
      if (quotation && quotation.priceUuid) {
        await queryPriceLclByKey({ key: quotation.priceUuid }).then(res => { priceLcl = res.data })
      }
      // 生成海运费费用
      await PriceLclUtil.generatingCharge(quotation, job, priceLcl)
      // 其他费用
      await PriceLclUtil.getQuotationChargeDetail(quotation, job, true, priceLcl)
      await PriceLclUtil.getQuotationChargeDetail(quotation, job, false, priceLcl)
    } else if (quotation.type === PriceConstant.QUOTATION_AIR) { // 空运
      job.lob = 'AE'
      quotation.quotationAirDetail = new QuotationAirDetail(quotationDetail)
      // 应收费用以及其他费用
      await PriceAirUtil.generatingReceivableCharge(quotation, job)
      // 应付费用
      await PriceAirUtil.generatingPayableCharge(quotation, job)
    }
    return true
  }

  /**
   * 基本信息
   */
  static getQuotationBasinInfo(quotation, job, quotationDetail) {
    // 业务类型
    job.lob = 'SE'
    // 订舱代理
    job.bookingAgent = quotation.bookingAgent
    // 船公司
    if (quotationDetail && quotationDetail.carrier) {
      if (quotationDetail.carrier instanceof Array) {
        job.carrierId = quotationDetail.carrier[0]
      } else {
        job.carrierId = quotationDetail.carrier
      }
    }
    // 发货地
    job.dispatchingPlace = quotation.receiptPlace
    // 装货港
    job.loadingPortCode = quotation.pol
    // 卸货港
    job.dischargingPortCode = quotation.pod
    // 目的港
    job.destinationPortCode = quotation.deliveryPlace
    // 中文品名
    job.cargoChineseName = quotation.goodsName
    // 英文品名
    job.cargoEnglishName = quotation.goodsName
    // HS Code
    job.hsCode = quotation.hsCode
    // 重量
    job.weightContract = quotation.grossWeight
    // 体积
    job.cubageContract = quotation.cubage
    // 尺寸
    job.quantityContract = parseInt(quotation.size)
    // 计费重量
    job.chargeWeight = quotation.chargeWeight
    /**
     * 件数
     * CARGO-2507:added by Deep Lau 2016-05-03
     */
    job.quantityContract = quotation.quantity
    job.quotationUuid = quotation.uuid // 临时属性
    // 备注
    job.remarks = quotation.remarks
  }

  /**
   * 生成应收费用
   */
  static async generateArChargeByQuotationFCL(quotationDetail, quotation, job) {
    let charge = null
    const chargeItem = getObjectByCache(basicCacheKeys.basic_data_ChargeItem, 'scaleCode', 'OF')
    if (!chargeItem) {
      Message.warning('收费项目中未找到平台代码为OF的海运费!')
      return false
    }
    if (quotation) {
      if (quotation.num20 > 0 && !StrUtils.isEmpty(quotationDetail.eq20)) { // num20
        charge = new Charge().fillDefaultValue().getData()
        charge.chargeItemId = chargeItem.itemId// 32; //海运费
        charge.currency = quotationDetail.currency
        charge.measure = ChargeMeasure.C20_CHARGE_MEASURE.code // "C20"; //单位
        charge.quantity = quotation.num20 // 数量
        charge.unitRate = parseInt(quotationDetail.eq20)
        charge.taxRate = quotationDetail.vat
        charge.taxAmount = charge.unitRate * charge.taxRate
        await this.getNewCharge(charge, job, true)
        await this.getJobSeaEq(job, ChargeMeasure.C20_CHARGE_MEASURE.englishName, quotationDetail.eqType, quotation.num20)
      }
      if (quotation.num40 > 0 && !StrUtils.isEmpty(quotationDetail.eq40)) { // num40
        charge = new Charge().fillDefaultValue().getData()
        charge.chargeItemId = chargeItem.itemId // 海运费
        charge.currency = quotationDetail.currency
        charge.measure = ChargeMeasure.C40_CHARGE_MEASURE.code // "C40"; //单位
        charge.quantity = quotation.num40 // 数量
        charge.unitRate = parseInt(quotationDetail.eq40)
        await this.getNewCharge(charge, job, true)
        await this.getJobSeaEq(job, ChargeMeasure.C40_CHARGE_MEASURE.englishName, quotationDetail.eqType, quotation.num40)
      }
      if (quotation.numHc > 0 && !StrUtils.isEmpty(quotationDetail.eqHc)) { // 40HC
        charge = new Charge().fillDefaultValue().getData()
        charge.chargeItemId = chargeItem.itemId // 海运费
        charge.currency = quotationDetail.currency
        charge.measure = ChargeMeasure.C40H_CHARGE_MEASURE.code // 单位
        charge.quantity = quotation.numHc // 数量
        charge.unitRate = parseInt(quotationDetail.eqHc)
        await this.getNewCharge(charge, job, true)
        await this.getJobSeaEq(job, ChargeMeasure.C40_CHARGE_MEASURE.englishName, 'HC', quotation.numHc)
      }
      if (quotation.num45 > 0 && !StrUtils.isEmpty(quotationDetail.eq45)) { // num45
        charge = new Charge().fillDefaultValue().getData()
        charge.chargeItemId = chargeItem.itemId // 海运费
        charge.currency = quotationDetail.currency
        charge.measure = ChargeMeasure.C45_CHARGE_MEASURE.code // 单位
        charge.quantity = quotation.num45 // 数量
        charge.unitRate = parseInt(quotationDetail.eq45)
        await this.getNewCharge(charge, job, true)
        await this.getJobSeaEq(job, ChargeMeasure.C45_CHARGE_MEASURE.englishName, quotationDetail.eqType, quotation.num45)
      }
    }
    return true
  }

  /**
   * 生成应收费用(港前 尾程)
   */
  static async generateChargeByQuotationDetail(quotationDetail, inquiry, job, quotation) {
    let charge = null
    for (const seaEq of inquiry.seaEqList) {
      if (quotationDetail.byEqSizeChargingItems && quotationDetail.byEqSizeChargingItems.length > 0) {
        const seaEqObj = SystemConstants.SEA_EQ_TYPE_MAP.get(seaEq.type)
        const byEqSizeChargingItems = quotationDetail.byEqSizeChargingItems.filter(p => !!p[seaEqObj.field] || p[seaEqObj.field] === 0)
        if (byEqSizeChargingItems && byEqSizeChargingItems.length > 0) {
          for (const item of byEqSizeChargingItems) {
            charge = new Charge().fillDefaultValue().getData()
            charge.chargeItemId = item.chargeItemId// 32; //海运费
            charge.currency = item.currency
            charge.measure = seaEqObj.measure // "C20"; //按照对应箱型带单位
            charge.quantity = seaEq.quantity // 数量
            charge.unitRate = parseInt(item[seaEqObj.field])
            if (quotation.type === PriceConstant.QUOTATION_PRE_PORT) {
              // 港前费用，结算对象取供应商
              charge.accountId = quotationDetail.supplier
            } else if (quotation.type === PriceConstant.QUOTATION_LAST_MILE) {
              // 尾程费用，结算对象取代理名称
              charge.accountId = quotationDetail.agentName
            }
            charge.createdBy = quotationDetail.prcCreatedBy // 运价创建人
            await this.getNewChargeForInquiry(charge, job, false)
          }
        }
      }
    }
    if (quotationDetail.notByEqSizeChargingItems && quotationDetail.notByEqSizeChargingItems.length > 0) {
      for (const item of quotationDetail.notByEqSizeChargingItems) {
        if (this.checkNotBySizeChargingItem(quotation, item)) {
          charge = new Charge().fillDefaultValue().getData()
          charge.chargeItemId = item.chargeItemId
          charge.currency = item.currency
          charge.measure = item.measure // 按照设置的带单位
          charge.quantity = 1
          charge.unitRate = item.unitRate
          if (quotation.type === PriceConstant.QUOTATION_PRE_PORT) {
            // 港前费用，结算对象取供应商
            charge.accountId = quotationDetail.supplier
          } else if (quotation.type === PriceConstant.QUOTATION_LAST_MILE) {
            // 尾程费用，结算对象取代理名称
            charge.accountId = quotationDetail.agentName
          }
          charge.createdBy = quotationDetail.prcCreatedBy // 运价创建人
          await this.getNewChargeForInquiry(charge, job, false)
        }
      }
    }
    return true
  }

  /**
   * 生成应付费用
   */
  static async generateApChargeByQuotationFCL(quotationDetail, quotation, job) {
    let charge = null
    const chargeItem = getObjectByCache(basicCacheKeys.basic_data_ChargeItem, 'scaleCode', 'OF')
    if (quotation.num20 > 0 && !StrUtils.isEmpty(quotationDetail.cost20)) { // num20
      charge = new Charge().fillDefaultValue().getData()
      charge.chargeItemId = chargeItem.itemId// 32; //海运费
      charge.currency = quotationDetail.currency
      charge.measure = ChargeMeasure.C20_CHARGE_MEASURE.code // "C20"; //单位
      charge.quantity = quotation.num20 // 数量
      charge.unitRate = parseInt(quotationDetail.cost20)
      charge.taxRate = quotationDetail.vat
      charge.taxAmount = charge.unitRate * charge.taxRate
      await this.getNewCharge(charge, job, false)
    }
    if (quotation.num40 > 0 && !StrUtils.isEmpty(quotationDetail.cost40)) { // num40
      charge = new Charge().fillDefaultValue().getData()
      charge.chargeItemId = chargeItem.itemId // 海运费
      charge.currency = quotationDetail.currency
      charge.measure = ChargeMeasure.C40_CHARGE_MEASURE.code // "C40"; //单位
      charge.quantity = quotation.num40 // 数量
      charge.unitRate = parseInt(quotationDetail.cost40)
      await this.getNewCharge(charge, job, false)
    }
    if (quotation.numHc > 0 && !StrUtils.isEmpty(quotationDetail.costHc)) { // 40HC
      charge = new Charge().fillDefaultValue().getData()
      charge.chargeItemId = chargeItem.itemId // 海运费
      charge.currency = quotationDetail.currency
      charge.measure = ChargeMeasure.C40H_CHARGE_MEASURE.code // 单位
      charge.quantity = quotation.numHc // 数量
      charge.unitRate = parseInt(quotationDetail.costHc)
      await this.getNewCharge(charge, job, false)
    }
    if (quotation.num45 > 0 && !StrUtils.isEmpty(quotationDetail.cost45)) { // num45
      charge = new Charge().fillDefaultValue().getData()
      charge.chargeItemId = chargeItem.itemId // 海运费
      charge.currency = quotationDetail.currency
      charge.measure = ChargeMeasure.C45_CHARGE_MEASURE.code // 单位
      charge.quantity = quotation.num45 // 数量
      charge.unitRate = parseInt(quotationDetail.cost45)
      await this.getNewCharge(charge, job, false)
    }
    return true
  }

  /**
   * 生成费用(通过成本)
   */
  static async generateChargeByQuotationFCLFromInquiry(quotationDetail, quotation, job, isReceivable) {
    let charge = null
    const chargeItem = getObjectByCache(basicCacheKeys.basic_data_ChargeItem, 'scaleCode', 'OF')
    if (quotation.num20 > 0 && !StrUtils.isEmpty(quotationDetail.cost20)) { // num20
      charge = new Charge().fillDefaultValue().getData()
      charge.chargeItemId = chargeItem ? chargeItem.itemId : 0// 32; //海运费
      charge.currency = quotationDetail.currency
      charge.measure = ChargeMeasure.C20_CHARGE_MEASURE.code // "C20"; //单位
      charge.quantity = quotation.num20 // 数量
      charge.unitRate = parseInt(quotationDetail.cost20)
      charge.taxRate = quotationDetail.vat
      charge.taxAmount = charge.unitRate * charge.taxRate
      charge.accountId = quotationDetail.source // 取 运价上的-运价来源 即 报价上的-订舱代理
      charge.createdBy = quotationDetail.prcCreatedBy // 运价创建人
      await this.getNewChargeForInquiry(charge, job, isReceivable)
    }
    if (quotation.num40 > 0 && !StrUtils.isEmpty(quotationDetail.cost40)) { // num40
      charge = new Charge().fillDefaultValue().getData()
      charge.chargeItemId = chargeItem ? chargeItem.itemId : 0// 32; //海运费
      charge.currency = quotationDetail.currency
      charge.measure = ChargeMeasure.C40_CHARGE_MEASURE.code // "C40"; //单位
      charge.quantity = quotation.num40 // 数量
      charge.unitRate = parseInt(quotationDetail.cost40)
      charge.accountId = quotationDetail.source // 取 运价上的-运价来源 即 报价上的-订舱代理
      charge.createdBy = quotationDetail.prcCreatedBy // 运价创建人
      await this.getNewChargeForInquiry(charge, job, isReceivable)
    }
    if (quotation.numHc > 0 && !StrUtils.isEmpty(quotationDetail.costHc)) { // 40HC
      charge = new Charge().fillDefaultValue().getData()
      charge.chargeItemId = chargeItem ? chargeItem.itemId : 0// 32; //海运费
      charge.currency = quotationDetail.currency
      charge.measure = ChargeMeasure.C40H_CHARGE_MEASURE.code // 单位
      charge.quantity = quotation.numHc // 数量
      charge.unitRate = parseInt(quotationDetail.costHc)
      charge.accountId = quotationDetail.source // 取 运价上的-运价来源 即 报价上的-订舱代理
      charge.createdBy = quotationDetail.prcCreatedBy // 运价创建人
      await this.getNewChargeForInquiry(charge, job, isReceivable)
    }
    if (quotation.num45 > 0 && !StrUtils.isEmpty(quotationDetail.cost45)) { // num45
      charge = new Charge().fillDefaultValue().getData()
      charge.chargeItemId = chargeItem ? chargeItem.itemId : 0// 32; //海运费
      charge.currency = quotationDetail.currency
      charge.measure = ChargeMeasure.C45_CHARGE_MEASURE.code // 单位
      charge.quantity = quotation.num45 // 数量
      charge.unitRate = parseInt(quotationDetail.cost45)
      charge.accountId = quotationDetail.source // 取 运价上的-运价来源 即 报价上的-订舱代理
      charge.createdBy = quotationDetail.prcCreatedBy // 运价创建人
      await this.getNewChargeForInquiry(charge, job, isReceivable)
    }
    if (quotation.num20Nor > 0 && !StrUtils.isEmpty(quotationDetail.cost20nor)) { // num20Nor
      charge = new Charge().fillDefaultValue().getData()
      charge.chargeItemId = chargeItem ? chargeItem.itemId : 0// 32; //海运费
      charge.currency = quotationDetail.currency
      charge.measure = ChargeMeasure.BY_CNTR_CHARGE_MEASURE.code // 按箱型
      charge.quantity = quotation.num20Nor // 数量
      charge.unitRate = parseInt(quotationDetail.cost20nor)
      charge.accountId = quotationDetail.source // 取 运价上的-运价来源 即 报价上的-订舱代理
      charge.createdBy = quotationDetail.prcCreatedBy // 运价创建人
      await this.getNewChargeForInquiry(charge, job, isReceivable)
    }
    if (quotation.num40Nor > 0 && !StrUtils.isEmpty(quotationDetail.cost40nor)) { // num40Nor
      charge = new Charge().fillDefaultValue().getData()
      charge.chargeItemId = chargeItem ? chargeItem.itemId : 0// 32; //海运费
      charge.currency = quotationDetail.currency
      charge.measure = ChargeMeasure.BY_CNTR_CHARGE_MEASURE.code // 单位
      charge.quantity = quotation.num40Nor // 数量
      charge.unitRate = parseInt(quotationDetail.cost40nor)
      charge.accountId = quotationDetail.source // 取 运价上的-运价来源 即 报价上的-订舱代理
      charge.createdBy = quotationDetail.prcCreatedBy // 运价创建人
      await this.getNewChargeForInquiry(charge, job, isReceivable)
    }
    return true
  }

  /**
   * 箱型箱量
   */
  static getJobSeaEq(job, eqSize, eqType, eqQuantity) {
    const seqEq = new SeaEQ().fillDefaultValue().getData()
    seqEq.jobUuid = job.uuid
    if (!StrUtils.isEmpty(eqSize)) {
      seqEq.eqSize = eqSize.replace("'", '')// 箱型
    }
    seqEq.eqType = eqType// 箱种
    seqEq.eqQuantity = eqQuantity// 箱量
    if (!job.seaEQ || job.seaEQ.length <= 0) job.seaEQ = []
    job.seaEQ.push(seqEq)
  }

  /**
   * 拼箱运价信息
   */
  static getQuotationLCLDetail(quotationLCLDetail, job) {
    return true
  }

  /**
   * 空运运价信息
   */
  static getQuotationAirDetail(quotationAirDetail, job) {
    return true
  }

  /**
   * 附加费，本地费用，目的地费用
   */
  static async getQuotationChargeDetail(quotation, job, isReceivable) {
    let charge = null
    if (quotation.quotationChargeDetail && quotation.quotationChargeDetail.length > 0) {
      for (const chargeDetail of quotation.quotationChargeDetail) {
        if (chargeDetail.type === 0 || chargeDetail.type === 1) { // 收货地,交货地费用
          if (chargeDetail.num > 0 && this.checkQuotationNum(quotation, chargeDetail)) {
            charge = new Charge().fillDefaultValue().getData()
            charge.chargeItemId = chargeDetail.chargeId
            charge.currency = chargeDetail.currency
            charge.measure = chargeDetail.unit
            charge.quantity = chargeDetail.num
            charge.unitRate = isReceivable ? chargeDetail.rate : chargeDetail.costRate
            charge.remarks = chargeDetail.remarks
            await this.getNewCharge(charge, job, isReceivable)
          }
        }
      }
    }
  }

  /**
   * 附加费，本地费用，目的地费用
   */
  static async getQuotationChargeDetailFromInquiry(quotation, job, isReceivable, accountId, prcCreatedBy) {
    let charge = null
    if (quotation.quotationChargeDetail && quotation.quotationChargeDetail.length > 0) {
      for (const chargeDetail of quotation.quotationChargeDetail) {
        if (chargeDetail.num > 0 && this.checkQuotationNum(quotation, chargeDetail)) {
          charge = new Charge().fillDefaultValue().getData()
          charge.chargeItemId = chargeDetail.chargeId
          charge.currency = chargeDetail.currency
          charge.measure = chargeDetail.unit
          charge.quantity = chargeDetail.num
          charge.unitRate = isReceivable ? chargeDetail.rate : chargeDetail.costRate
          charge.remarks = chargeDetail.remarks
          charge.accountId = accountId // 取 运价上的-运价来源 即 报价上的-订舱代理
          charge.createdBy = prcCreatedBy
          await this.getNewChargeForInquiry(charge, job, isReceivable)
        }
      }
    }
  }

  /**
   * @private
   */
  static checkQuotationNum(quotation, detail) {
    console.log('[BookingUtil] checkQuotationNum', {
      unit: detail.unit,
      num20: quotation.num20,
      num40: quotation.num40,
      numHc: quotation.numHc,
      num45: quotation.num45,
      quotationChargeDetailLen: quotation && quotation.quotationChargeDetail && quotation.quotationChargeDetail.length
    })
    if (detail.unit !== ChargeMeasure.C20_CHARGE_MEASURE.code && detail.unit !== ChargeMeasure.C40_CHARGE_MEASURE.code && detail.unit !== ChargeMeasure.C40H_CHARGE_MEASURE.code && detail.unit !== ChargeMeasure.C45_CHARGE_MEASURE.code) {
      return true
    }
    if (quotation.num20 > 0 && detail.unit === ChargeMeasure.C20_CHARGE_MEASURE.code) {
      return true
    }
    if (quotation.num40 > 0 && detail.unit === ChargeMeasure.C40_CHARGE_MEASURE.code) {
      return true
    }
    if (quotation.numHc > 0 && detail.unit === ChargeMeasure.C40H_CHARGE_MEASURE.code) {
      return true
    }
    if (quotation.num45 > 0 && detail.unit === ChargeMeasure.C45_CHARGE_MEASURE.code) {
      return true
    }
    return false
  }

  /**
   * @private
   */
  static checkNotBySizeChargingItem(quotation, detail) {
    if (detail.measure !== ChargeMeasure.C20_CHARGE_MEASURE.code &&
        detail.measure !== ChargeMeasure.C40_CHARGE_MEASURE.code &&
        detail.measure !== ChargeMeasure.C40H_CHARGE_MEASURE.code &&
        detail.measure !== ChargeMeasure.C45_CHARGE_MEASURE.code) {
      return true
    }
    if (quotation.num20 > 0 && detail.measure === ChargeMeasure.C20_CHARGE_MEASURE.code) {
      return true
    }
    if (quotation.num40 > 0 && detail.measure === ChargeMeasure.C40_CHARGE_MEASURE.code) {
      return true
    }
    if (quotation.numHc > 0 && detail.measure === ChargeMeasure.C40H_CHARGE_MEASURE.code) {
      return true
    }
    if (quotation.num45 > 0 && detail.measure === ChargeMeasure.C45_CHARGE_MEASURE.code) {
      return true
    }
    return false
  }

  /**
   * @private
   */
  static async getNewCharge(charge, job, isReceivable) {
    charge.jobUuid = job.uuid
    charge.businessDate = job.businessDate
    charge.inputBy = $store.getters.userId
    charge.inputDate = new Date()
    const department = $store.getters.currentDept
    const office = $store.getters.currentOffice
    charge.inputByDep = !department ? 0 : department.departmentId
    charge.branchId = !office ? 0 : office.officeId
    const localCurrency = RateUtil.localCurrencyByOfficeId(charge.branchId)
    charge.localCurrency = localCurrency
    charge.exchangeRate = await RateUtil.getForeignExchangeRateByJob(localCurrency, charge.currency, job, charge.branchId, function(rate) {
      charge.exchangeRate = rate
    })
    // HQ币种/汇率
    charge.hqCurrency = RateUtil.getHqCurrency(charge.branchId)
    charge.hqRate = await RateUtil.getForeignExchangeRateByJob(charge.hqCurrency, localCurrency, job, charge.branchId)
    // 单价是否含税
    charge.unitPriceIncludeTax = parseInt(cache.config.get('InputIncludingTaxUnitPrice')) === 2
    if (charge.unitPriceIncludeTax) {
      ChargeUtils.calculateTaxAmount(charge, multiply(charge.unitRate, charge.quantity), charge.taxRate)
    } else {
      ChargeUtils.calculateTax(charge, multiply(charge.unitRate, charge.quantity), charge.taxRate)
    }
    charge.verifiedAmount = 0
    charge.receivable = isReceivable
    if (!isReceivable) {
      charge.accountId = job.bookingAgent > 0 ? job.bookingAgent : job.carrierId
      if (charge.accountId && charge.accountId > 0) {
        const company = await Company.getCompanyById(charge.accountId)
        if (company) {
          charge.oversea = (company.designationAgent || company.overseaAgent) // 指定货代理/目的港代理
        }
      }
    }
    if (!job.charges || job.charges.length <= 0) job.charges = []
    job.charges.push(charge)
  }

  /**
   * @private
   */
  static async getNewChargeForInquiry(charge, job, isReceivable) {
    charge.jobUuid = job.uuid
    charge.businessDate = job.businessDate
    charge.inputBy = $store.getters.userId
    charge.inputDate = new Date()
    const department = $store.getters.currentDept
    const office = $store.getters.currentOffice
    charge.inputByDep = !department ? 0 : department.departmentId
    charge.branchId = !office ? 0 : office.officeId
    const localCurrency = RateUtil.localCurrencyByOfficeId(charge.branchId)
    charge.localCurrency = localCurrency
    charge.exchangeRate = await RateUtil.getForeignExchangeRateByJob(localCurrency, charge.currency, job, charge.branchId, function(rate) {
      charge.exchangeRate = rate
    })
    // HQ币种/汇率
    charge.hqCurrency = RateUtil.getHqCurrency(charge.branchId)
    charge.hqRate = await RateUtil.getForeignExchangeRateByJob(charge.hqCurrency, localCurrency, job, charge.branchId)
    // 单价是否含税
    charge.unitPriceIncludeTax = parseInt(cache.config.get('InputIncludingTaxUnitPrice')) === 2
    if (charge.unitPriceIncludeTax) {
      ChargeUtils.calculateTaxAmount(charge, multiply(charge.unitRate, charge.quantity), charge.taxRate)
    } else {
      ChargeUtils.calculateTax(charge, multiply(charge.unitRate, charge.quantity), charge.taxRate)
    }
    charge.businessQuotationAmount = charge.amount
    charge.businessQuotationConfirmd = true
    charge.verifiedAmount = 0
    charge.receivable = isReceivable
    if (!job.charges || job.charges.length <= 0) job.charges = []
    job.charges.push(charge)
  }
}
export default BookingUtil
